import { useTranslation } from 'react-i18next';
import ColumnHeaderFilter from '../common/ColumnHeaderFilter';
import { SearchableMultiSelect, type SearchableMultiSelectOption } from './SearchableMultiSelect';
import { EMPTY_SELECTION, NO_VALUE_TOKEN, isLookupActive, lookupSummary, type LookupSelection } from './lookupFilter';

/**
 * A lookup column's header filter (fleet filter contract v3 §16.8 / C86, adopted 2026-11-03): this repo's staging
 * `ColumnHeaderFilter` (I1–I5: the draft commits only on Apply, or Enter) hosting the fleet's shared `SearchableMultiSelect`,
 * copied byte-identical from ptc-core @ 7d1f5f9. Search, facet counts, Select all / Clear, "Exclude these" and "(Has no value)",
 * every word translated here.
 */
export interface LookupColumnFilterProps {
  /** Already-translated column label. */
  columnLabel: string;
  options: SearchableMultiSelectOption[];
  /** The APPLIED selection. */
  value: LookupSelection;
  onApply: (next: LookupSelection) => void;
  /** Per-option counts over the rows matching every OTHER filter (`facetCountsFor`), with "(has no value)" under NO_VALUE_TOKEN. */
  facetCounts?: Record<string, number>;
  align?: 'start' | 'end';
}

export function LookupColumnFilter({ columnLabel, options, value, onApply, facetCounts, align }: LookupColumnFilterProps) {
  const { t } = useTranslation();
  const noValueLabel = t('common.lookupFilter.hasNoValue');
  return (
    <ColumnHeaderFilter
      columnLabel={columnLabel}
      value={value}
      emptyValue={EMPTY_SELECTION}
      isActive={isLookupActive}
      onApply={onApply}
      activeValueLabel={lookupSummary(value, options, noValueLabel, t)}
      align={align}
    >
      {(draft, setDraft, apply) => (
        <SearchableMultiSelect
          label={columnLabel}
          options={options}
          selected={draft.codes}
          onChange={(codes) => setDraft((d) => ({ ...d, codes }))}
          facetCounts={facetCounts}
          noValueOption={{
            label: noValueLabel,
            checked: draft.includeNoValue,
            count: facetCounts?.[NO_VALUE_TOKEN],
            onChange: (checked) => setDraft((d) => ({ ...d, includeNoValue: checked })),
          }}
          excludeToggle={{
            label: t('common.lookupFilter.exclude'),
            checked: draft.excluding,
            onChange: (checked) => setDraft((d) => ({ ...d, excluding: checked })),
          }}
          searchPlaceholder={t('common.lookupFilter.searchPlaceholder')}
          labels={{
            selectAll: t('common.lookupFilter.selectAll'),
            clear: t('common.lookupFilter.clear'),
            noMatches: t('common.lookupFilter.noMatches'),
            valueSuffix: t('common.lookupFilter.valueSuffix'),
            searchSuffix: t('common.lookupFilter.searchSuffix'),
          }}
          onEnter={apply}
        />
      )}
    </ColumnHeaderFilter>
  );
}
